import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Plus, Search, FileText, CheckCircle2, XCircle, Clock, RefreshCw,
  Download, X, AlertCircle, Loader2, Eye, ExternalLink, Ban,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface DocFiscal {
  id: number
  tipo: string
  pedido_id: number | null
  numero: string | null
  serie: string | null
  chave: string | null
  status: string
  valor_total: number | string
  ambiente: string
  mensagem: string | null
  xml_url: string | null
  pdf_url: string | null
  criado_em: string
  emitido_em: string | null
}

const STATUS_META: Record<string, { label: string; cor: string; icon: any }> = {
  pendente: { label: 'Pendente', cor: 'bg-amber-100 text-amber-800', icon: Clock },
  processando: { label: 'Processando', cor: 'bg-blue-100 text-blue-800', icon: RefreshCw },
  autorizado: { label: 'Autorizada', cor: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  rejeitado: { label: 'Rejeitada', cor: 'bg-red-100 text-red-800', icon: XCircle },
  cancelado: { label: 'Cancelada', cor: 'bg-ink-100 text-ink-700', icon: Ban },
}

const TIPO_META: Record<string, string> = {
  NFe: 'NF-e 55',
  NFCe: 'NFC-e 65',
  NFSe: 'NFS-e',
  CFe: 'CF-e',
}

export default function FiscalPage() {
  const qc = useQueryClient()
  const [filtroStatus, setFiltroStatus] = useState<string>('todos')
  const [busca, setBusca] = useState('')
  const [selDoc, setSelDoc] = useState<DocFiscal | null>(null)
  const [showInut, setShowInut] = useState(false)
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 100

  const { data: docs = [], isLoading } = useQuery<DocFiscal[]>({
    queryKey: ['fiscal-docs'],
    queryFn: async () => (await api.get('/fiscal/documentos')).data,
    refetchInterval: 30000,
  })

  const lista = docs.filter(d => {
    if (filtroStatus !== 'todos' && d.status !== filtroStatus) return false
    if (busca) {
      const q = busca.toLowerCase()
      return (
        (d.chave || '').toLowerCase().includes(q) ||
        (d.numero || '').toLowerCase().includes(q) ||
        String(d.pedido_id || '').includes(q)
      )
    }
    return true
  })

  const totalPages = Math.max(1, Math.ceil(lista.length / PAGE_SIZE))
  const pageAtual = Math.min(page, totalPages)
  const pageItems = lista.slice((pageAtual - 1) * PAGE_SIZE, pageAtual * PAGE_SIZE)

  const counts = {
    todos: docs.length,
    pendente: docs.filter(d => d.status === 'pendente').length,
    autorizado: docs.filter(d => d.status === 'autorizado').length,
    rejeitado: docs.filter(d => d.status === 'rejeitado').length,
    cancelado: docs.filter(d => d.status === 'cancelado').length,
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FileText size={22} /> Documentos Fiscais
          </h1>
          <p className="text-sm text-ink-500">
            Notas fiscais emitidas, em processamento ou rejeitadas pela SEFAZ.
          </p>
        </div>
        <button onClick={() => setShowInut(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-sm font-medium">
          <Ban size={15} /> Inutilizar numeração
        </button>
      </div>
      {showInut && <InutilizarModal onClose={() => setShowInut(false)} />}

      {/* Filtros e busca */}
      <div className="flex gap-2 flex-wrap">
        {[
          { v: 'todos',       t: 'Todos',        cnt: counts.todos },
          { v: 'pendente',    t: 'Pendentes',    cnt: counts.pendente },
          { v: 'autorizado',  t: 'Autorizadas',  cnt: counts.autorizado },
          { v: 'rejeitado',   t: 'Rejeitadas',   cnt: counts.rejeitado },
          { v: 'cancelado',   t: 'Canceladas',   cnt: counts.cancelado },
        ].map(f => (
          <button key={f.v} onClick={() => { setFiltroStatus(f.v); setPage(1) }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
              filtroStatus === f.v
                ? 'bg-brand-600 text-white border-brand-600'
                : 'bg-white text-ink-700 border-ink-200 hover:bg-ink-50'
            }`}>
            {f.t} <span className="text-xs opacity-70">({f.cnt})</span>
          </button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            value={busca} onChange={e => { setBusca(e.target.value); setPage(1) }}
            onKeyDown={enterAbrePrimeiro(lista, d => setSelDoc(d))}
            placeholder="Buscar chave, número, pedido…"
            className="input pl-9 w-64" />
        </div>
      </div>

      {/* Lista */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Status</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Tipo</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Nº/Série</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Chave</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Pedido</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Valor</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Amb.</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-ink-600 uppercase">Emissão</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={9} className="text-center py-6 text-ink-400">Carregando…</td></tr>}
            {!isLoading && lista.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-ink-400">
                Nenhuma nota fiscal. Emita via pedido confirmado.
              </td></tr>
            )}
            {pageItems.map(d => {
              const meta = STATUS_META[d.status] || { label: d.status, cor: 'bg-gray-100 text-gray-700', icon: FileText }
              const Icon = meta.icon
              return (
                <tr key={d.id} className="hover:bg-violet-50 cursor-pointer" onClick={() => setSelDoc(d)}>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${meta.cor}`}>
                      <Icon size={10} /> {meta.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-medium">{TIPO_META[d.tipo] || d.tipo}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {d.numero ? `${d.numero}/${d.serie || '1'}` : '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-ink-500">
                    {d.chave ? `${d.chave.slice(0, 8)}...${d.chave.slice(-4)}` : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {d.pedido_id ? (
                      <Link to="/vendas" onClick={e => e.stopPropagation()}
                        className="text-brand-600 hover:underline text-xs">#{d.pedido_id}</Link>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-medium">{formatMoney(d.valor_total)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                      d.ambiente === 'producao' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {d.ambiente === 'producao' ? 'PROD' : 'HOM'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-500">
                    {d.emitido_em
                      ? new Date(d.emitido_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                      : new Date(d.criado_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  <td className="px-3 py-2">
                    <Eye size={14} className="text-ink-400" />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Paginação 100/página */}
      {lista.length > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-ink-500">
            Mostrando {(pageAtual - 1) * PAGE_SIZE + 1}–{Math.min(pageAtual * PAGE_SIZE, lista.length)} de {lista.length}
          </span>
          <div className="flex items-center gap-2">
            <button disabled={pageAtual <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg border border-ink-200 bg-white hover:bg-ink-50 disabled:opacity-40">Anterior</button>
            <span className="text-ink-600">Página {pageAtual} de {totalPages}</span>
            <button disabled={pageAtual >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 rounded-lg border border-ink-200 bg-white hover:bg-ink-50 disabled:opacity-40">Próxima</button>
          </div>
        </div>
      )}

      {/* Drawer detalhes */}
      {selDoc && <DocDrawer doc={selDoc} onClose={() => setSelDoc(null)}
        onUpdated={() => qc.invalidateQueries({ queryKey: ['fiscal-docs'] })} />}
    </div>
  )
}

// ============ DRAWER DE DETALHES ============
function DocDrawer({ doc, onClose, onUpdated }: { doc: DocFiscal; onClose: () => void; onUpdated: () => void }) {
  const [justif, setJustif] = useState('')
  const [showCancelar, setShowCancelar] = useState(false)

  const cancelar = useMutation({
    mutationFn: async () =>
      api.post(`/fiscal/documentos/${doc.id}/cancelar?justificativa=${encodeURIComponent(justif)}`),
    onSuccess: () => {
      toast.success('NF cancelada')
      onUpdated()
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cancelar'),
  })

  const reenviar = useMutation({
    mutationFn: async () => (await api.post(`/nfe-proprio/emitir/${doc.pedido_id}`)).data,
    onSuccess: (d: any) => {
      if (d?.autorizada) toast.success(`NF-e autorizada${d.numero ? ` — nº ${d.numero}/${d.serie}` : ''}`)
      else toast.error(`SEFAZ ${d?.cStat || ''}: ${d?.xMotivo || d?.erro || 'não autorizada'}`)
      onUpdated()
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao reenviar'),
  })

  const meta = STATUS_META[doc.status] || { label: doc.status, cor: 'bg-gray-100', icon: FileText }
  const Icon = meta.icon

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white z-50 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 rounded hover:bg-ink-100"><X size={18} /></button>
            <h2 className="font-semibold">{TIPO_META[doc.tipo] || doc.tipo} · {doc.numero ? `#${doc.numero}` : 'Sem número'}</h2>
          </div>
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase px-2 py-0.5 rounded ${meta.cor}`}>
            <Icon size={12} /> {meta.label}
          </span>
        </div>

        <div className="p-5 space-y-4">
          {/* Chave */}
          {doc.chave && (
            <div>
              <div className="text-xs text-ink-500 uppercase mb-1">Chave de acesso</div>
              <div className="font-mono text-[11px] bg-ink-50 p-2 rounded border border-ink-200 break-all">
                {doc.chave}
              </div>
            </div>
          )}

          {/* Dados gerais */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Tipo" value={TIPO_META[doc.tipo] || doc.tipo} />
            <Info label="Ambiente" value={doc.ambiente === 'producao' ? '🔴 Produção' : '🟡 Homologação'} />
            <Info label="Valor" value={formatMoney(doc.valor_total)} />
            <Info label="Pedido" value={doc.pedido_id ? `#${doc.pedido_id}` : '—'} />
            <Info label="Criado em" value={new Date(doc.criado_em).toLocaleString('pt-BR')} />
            <Info label="Emitido em" value={doc.emitido_em ? new Date(doc.emitido_em).toLocaleString('pt-BR') : '—'} />
          </div>

          {/* Rejeição */}
          {doc.status === 'rejeitado' && doc.mensagem && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-900 font-semibold mb-1">
                <AlertCircle size={14} /> Motivo da rejeição
              </div>
              <p className="text-sm text-red-800 whitespace-pre-wrap">{doc.mensagem}</p>
              {doc.pedido_id && (
                <button onClick={() => reenviar.mutate()} disabled={reenviar.isPending}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                  {reenviar.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  Reenviar para a SEFAZ
                </button>
              )}
            </div>
          )}

          {/* Downloads */}
          {doc.status === 'autorizado' && (
            <div className="space-y-2">
              <div className="text-xs text-ink-500 uppercase">Arquivos</div>
              {(() => {
                const isUrlValida = (u: string | null) =>
                  !!u && !u.startsWith('/mock') && (u.startsWith('http') || u.startsWith('/uploads'))
                const pdfOk = isUrlValida(doc.pdf_url)
                const xmlOk = isUrlValida(doc.xml_url)
                const isMock = doc.pdf_url?.startsWith('/mock') || doc.xml_url?.startsWith('/mock')
                // Emissor proprio: tem pedido_id e o XML/DANFE vem dos endpoints autenticados
                // (api com responseType blob), nao de uma URL publica.
                const temProprio = !!doc.pedido_id
                const baixarDanfe = async () => {
                  try {
                    const resp = await api.get(`/nfe-proprio/danfe-autorizado/${doc.pedido_id}`, { responseType: 'blob' })
                    const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }))
                    window.open(url, '_blank')
                    setTimeout(() => URL.revokeObjectURL(url), 60000)
                  } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro ao gerar DANFE') }
                }
                const baixarXml = async () => {
                  try {
                    const resp = await api.get(`/nfe-proprio/xml-autorizado/${doc.pedido_id}`, { responseType: 'blob' })
                    const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/xml' }))
                    const a = document.createElement('a')
                    a.href = url; a.download = `${doc.chave || 'nfe'}.xml`; a.click()
                    setTimeout(() => URL.revokeObjectURL(url), 60000)
                  } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro ao baixar XML') }
                }
                return (
                  <>
                    {temProprio ? (
                      <button onClick={baixarDanfe}
                        className="flex items-center gap-2 w-full px-4 py-2 bg-brand-50 hover:bg-brand-100 text-brand-700 rounded-lg text-sm font-medium">
                        <Download size={14} /> Ver DANFE (PDF)
                        <ExternalLink size={12} className="ml-auto" />
                      </button>
                    ) : pdfOk ? (
                      <a href={doc.pdf_url!} target="_blank" rel="noreferrer"
                        className="flex items-center gap-2 w-full px-4 py-2 bg-brand-50 hover:bg-brand-100 text-brand-700 rounded-lg text-sm font-medium">
                        <Download size={14} /> Baixar DANFE (PDF)
                        <ExternalLink size={12} className="ml-auto" />
                      </a>
                    ) : (
                      <div className="flex items-center gap-2 w-full px-4 py-2 bg-ink-50 text-ink-400 rounded-lg text-sm font-medium cursor-not-allowed">
                        <Download size={14} /> DANFE (PDF) — indisponível
                      </div>
                    )}
                    {temProprio ? (
                      <button onClick={baixarXml}
                        className="flex items-center gap-2 w-full px-4 py-2 bg-ink-50 hover:bg-ink-100 text-ink-700 rounded-lg text-sm font-medium">
                        <Download size={14} /> Baixar XML autorizado
                      </button>
                    ) : xmlOk ? (
                      <a href={doc.xml_url!} target="_blank" rel="noreferrer"
                        className="flex items-center gap-2 w-full px-4 py-2 bg-ink-50 hover:bg-ink-100 text-ink-700 rounded-lg text-sm font-medium">
                        <Download size={14} /> Baixar XML autorizado
                        <ExternalLink size={12} className="ml-auto" />
                      </a>
                    ) : (
                      <div className="flex items-center gap-2 w-full px-4 py-2 bg-ink-50 text-ink-400 rounded-lg text-sm font-medium cursor-not-allowed">
                        <Download size={14} /> XML — indisponível
                      </div>
                    )}
                    {isMock && !temProprio && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
                        ⚠ Emissão feita com provider mock (dev). Arquivos reais só aparecem após configurar token Focus NFe no .env.
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
          )}

          {/* Cancelar */}
          {doc.status === 'autorizado' && !showCancelar && (
            <button onClick={() => setShowCancelar(true)}
              className="btn-secondary text-red-700 hover:bg-red-50 w-full">
              <Ban size={14} /> Cancelar NF
            </button>
          )}
          {showCancelar && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
              <label className="text-sm font-semibold text-red-900">
                Justificativa do cancelamento <span className="text-xs font-normal">(mín. 15 caracteres)</span>
              </label>
              <textarea value={justif} onChange={e => setJustif(e.target.value)} rows={3}
                className="input" placeholder="Ex: Pedido cancelado pelo cliente antes do envio." />
              <div className="flex gap-2">
                <button onClick={() => { setShowCancelar(false); setJustif('') }}
                  className="btn-secondary flex-1">Voltar</button>
                <button onClick={() => cancelar.mutate()}
                  disabled={justif.length < 15 || cancelar.isPending}
                  className="btn-primary flex-1 bg-red-600 hover:bg-red-700 border-red-600">
                  {cancelar.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Confirmar cancelamento'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function Info({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-xs text-ink-500 uppercase">{label}</div>
      <div className="font-medium text-sm">{value}</div>
    </div>
  )
}


function InutilizarModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [serie] = useState(4)
  const [ini, setIni] = useState('')
  const [fim, setFim] = useState('')
  const [justif, setJustif] = useState('')

  const { data: gaps, isLoading } = useQuery<any>({
    queryKey: ['numeracao-gaps', serie],
    queryFn: async () => (await api.get('/nfe-proprio/numeracao/gaps', { params: { serie } })).data,
  })

  const inutilizar = useMutation({
    mutationFn: async () => (await api.post('/nfe-proprio/inutilizar', {
      serie, num_ini: Number(ini), num_fin: Number(fim), justificativa: justif,
    })).data,
    onSuccess: (r: any) => {
      if (r.sucesso) { toast.success(`Inutilizado (prot ${r.protocolo || ''}) — ${r.xMotivo || ''}`); qc.invalidateQueries({ queryKey: ['numeracao-gaps'] }); setIni(''); setFim(''); setJustif('') }
      else toast.error(`Não inutilizou: ${r.xMotivo || r.erro || 'verifique'}`, { duration: 7000 })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao inutilizar'),
  })

  const podeEnviar = ini && fim && Number(fim) >= Number(ini) && justif.trim().length >= 15 && !inutilizar.isPending

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="font-semibold flex items-center gap-2"><Ban size={16} className="text-amber-600" /> Inutilizar numeração — Série {serie}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          {isLoading && <div className="text-center py-4 text-ink-400"><Loader2 className="animate-spin inline" /></div>}
          {gaps && (
            <>
              <div className="text-xs text-ink-600">Último número autorizado: <b>{gaps.max_autorizado}</b> · livres p/ inutilizar: <b>{gaps.livres_total}</b> · já inutilizados: {gaps.inutilizados_total}</div>
              <div>
                <div className="text-xs font-medium text-ink-500 mb-1">Faixas LIVRES (clique pra preencher):</div>
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto border rounded-lg p-2 bg-ink-50/50">
                  {(gaps.faixas || []).length === 0 && <span className="text-xs text-ink-400">Nenhuma faixa livre — sequência limpa ✓</span>}
                  {(gaps.faixas || []).map((f: any, i: number) => (
                    <button key={i} onClick={() => { setIni(String(f.ini)); setFim(String(f.fim)) }}
                      className="px-2 py-1 rounded bg-white border border-ink-200 text-xs hover:border-amber-400 hover:bg-amber-50">
                      {f.ini === f.fim ? f.ini : `${f.ini}–${f.fim}`} <span className="text-ink-400">({f.qtd})</span>
                    </button>
                  ))}
                </div>
              </div>
              {(gaps.ghosts_539 || []).length > 0 && (
                <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2 flex items-start gap-2">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>Números <b>{(gaps.ghosts_539 || []).join(', ')}</b> deram duplicidade (539): já estão <b>autorizados na SEFAZ</b>. NÃO inutilize — precisam ser recuperados/cancelados.</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><div className="text-xs font-medium mb-1">Número inicial</div>
                  <input type="number" className="input w-full" value={ini} onChange={e => setIni(e.target.value)} /></label>
                <label className="block"><div className="text-xs font-medium mb-1">Número final</div>
                  <input type="number" className="input w-full" value={fim} onChange={e => setFim(e.target.value)} /></label>
              </div>
              <label className="block"><div className="text-xs font-medium mb-1">Justificativa (mín. 15 caracteres)</div>
                <textarea className="input w-full" rows={2} value={justif} onChange={e => setJustif(e.target.value)}
                  placeholder="Ex.: Numeração saltada/rejeitada na emissão — inutilização para regularizar a sequência fiscal." /></label>
            </>
          )}
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button disabled={!podeEnviar} onClick={() => { if (confirm(`Inutilizar números ${ini} a ${fim} (série ${serie}) na SEFAZ? Não dá pra desfazer.`)) inutilizar.mutate() }}
            className="btn-primary bg-amber-600 hover:bg-amber-700">
            {inutilizar.isPending && <Loader2 size={14} className="animate-spin" />} Inutilizar na SEFAZ
          </button>
        </div>
      </div>
    </div>
  )
}
